/**
 * API Ogrodu marzeń — POST /api/economy/purchase {kind:'garden'}, visits, me.
 */
import {
  PurchaseRequestSchema,
  VisitsRequestSchema,
  VisitsResponseSchema,
  type PurchaseResponse,
  type VisitsResponse,
} from '@pieskowo/shared';
import { SaveLocal } from '../../core/SaveLocal.js';
import { scopedKey } from '../../core/profileScope.js';
import { fetchMe, newIdempotencyKey, postPurchase } from '../../avatar/index.js';
import { OutfitStore } from '../../avatar/outfitStore.js';
import {
  LOCAL_BALANCE_KEY,
  LOCAL_GARDEN_JSON_KEY,
  LOCAL_GARDEN_UNLOCKED_KEY,
  normalizeGardenJson,
  plantFlower,
  type PlantedFlower,
} from './gardenState.js';

function apiBase(): string {
  try {
    const v = import.meta.env?.VITE_API_URL;
    if (typeof v === 'string' && v.length > 0) return v.replace(/\/$/, '');
  } catch {
    /* test */
  }
  return '';
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = SaveLocal.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function loadGardenUnlocked(): boolean {
  return SaveLocal.getJson<boolean>(scopedKey(LOCAL_GARDEN_UNLOCKED_KEY), false) === true;
}

export function saveGardenUnlocked(v: boolean): void {
  SaveLocal.setJson(scopedKey(LOCAL_GARDEN_UNLOCKED_KEY), v);
}

export function loadGardenFlowers(): PlantedFlower[] {
  return normalizeGardenJson(
    SaveLocal.getJson<unknown>(scopedKey(LOCAL_GARDEN_JSON_KEY), []),
  );
}

export function saveGardenFlowers(flowers: PlantedFlower[]): void {
  SaveLocal.setJson(scopedKey(LOCAL_GARDEN_JSON_KEY), flowers);
}

export function loadBalance(): number {
  const fromOutfit = OutfitStore.load().balanceZl;
  if (Number.isInteger(fromOutfit) && fromOutfit > 0) return fromOutfit;
  const local = SaveLocal.getJson<number>(scopedKey(LOCAL_BALANCE_KEY), 0);
  return Number.isInteger(local) ? local : 0;
}

export function saveBalance(balanceZl: number): void {
  SaveLocal.setJson(scopedKey(LOCAL_BALANCE_KEY), balanceZl);
  OutfitStore.saveBalance(balanceZl);
}

export type GardenBootstrap = {
  unlocked: boolean;
  balanceZl: number;
  flowers: PlantedFlower[];
  source: 'server' | 'local';
};

export async function bootstrapGarden(): Promise<GardenBootstrap> {
  const flowers = loadGardenFlowers();
  const localUnlocked = loadGardenUnlocked();
  const localBalance = loadBalance();

  const me = await fetchMe();
  if (me.ok) {
    const unlocked = me.data.gardenUnlocked || localUnlocked;
    if (unlocked) saveGardenUnlocked(true);
    saveBalance(me.data.balanceZl);
    return {
      unlocked,
      balanceZl: me.data.balanceZl,
      flowers,
      source: 'server',
    };
  }

  return {
    unlocked: localUnlocked,
    balanceZl: localBalance,
    flowers,
    source: 'local',
  };
}

export type PurchaseGardenResult =
  | { ok: true; balanceZl: number; gardenUnlocked: true }
  | { ok: false; code: string };

/**
 * Zakup ogrodu — WYŁĄCZNIE przez serwer (§7.5: transakcje tylko serwer).
 * Bez sieci / 501 / INTERNAL: NIE odblokowujemy i NIE ruszamy salda lokalnie —
 * zwracamy `OFFLINE` = „poczekaj na sieć". Jedynym źródłem `gardenUnlocked`
 * i salda jest potwierdzona odpowiedź serwera (finding P1-4).
 */
export async function purchaseGarden(
  _balanceZl: number,
  alreadyUnlocked: boolean,
): Promise<PurchaseGardenResult> {
  if (alreadyUnlocked) {
    return { ok: false, code: 'ALREADY' };
  }

  const body = {
    kind: 'garden' as const,
    idempotencyKey: newIdempotencyKey(),
  };
  const req = PurchaseRequestSchema.safeParse(body);
  if (!req.success) return { ok: false, code: 'VALIDATION' };

  // preferuj shared postPurchase z avatar (ten sam endpoint)
  const remote = await postPurchase(req.data);
  if (remote.ok) {
    const data: PurchaseResponse = remote.data;
    const unlocked = data.gardenUnlocked ?? true;
    if (unlocked) saveGardenUnlocked(true);
    saveBalance(data.balance);
    return {
      ok: true,
      balanceZl: data.balance,
      gardenUnlocked: true,
    };
  }

  // offline / 501 / INTERNAL / NETWORK — brak potwierdzenia serwera.
  // NIE odblokowujemy, NIE odpisujemy salda: „poczekaj na sieć".
  if (
    remote.status === 0 ||
    remote.status === 501 ||
    remote.code === 'INTERNAL' ||
    remote.code === 'NETWORK' ||
    remote.code === 'OFFLINE' ||
    remote.code === 'NOT_IMPLEMENTED'
  ) {
    return { ok: false, code: 'OFFLINE' };
  }

  if (remote.code === 'INSUFFICIENT_FUNDS' || remote.status === 402) {
    return { ok: false, code: 'INSUFFICIENT_FUNDS' };
  }
  if (remote.status === 409 || remote.code === 'ALREADY') {
    saveGardenUnlocked(true);
    return { ok: false, code: 'ALREADY' };
  }
  return { ok: false, code: remote.code };
}

export function addFlowerLocal(
  current: PlantedFlower[],
  flower: PlantedFlower,
): PlantedFlower[] {
  const next = plantFlower(current, flower);
  saveGardenFlowers(next);
  return next;
}

export async function recordVisit(placeId: string): Promise<VisitsResponse | null> {
  const req = VisitsRequestSchema.safeParse({ placeId });
  if (!req.success) return null;
  try {
    const res = await fetch(`${apiBase()}/api/visits`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(req.data),
    });
    if (!res.ok) return null;
    const json: unknown = await res.json();
    const parsed = VisitsResponseSchema.safeParse(json);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

